import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2, KeyRound } from "lucide-react";
import { api, errMsg, fdate } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PageHeader, Hash, SimBadge, Loading, CopyText, LiveIndicator } from "@/components/common";

export default function Audit() {
  const ledger = useFetch("/legal/ledger?limit=150", { poll: 8000 });
  const events = useFetch("/webhooks/events?limit=100", { poll: 8000 });
  const pk = useFetch("/legal/public-key");
  const [chain, setChain] = useState(null);
  const [checking, setChecking] = useState(false);
  const verify = async () => {
    setChecking(true);
    try { const { data } = await api.get("/legal/ledger/verify"); setChain(data); toast[data.valid ? "success" : "error"](data.valid ? `Cadena íntegra: ${data.checked} bloques` : `Ruptura en bloque ${data.broken_at}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setChecking(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="Validez legal y seguridad" title="Auditoría e inmutabilidad" subtitle="Bitácora encadenada por hash de cada evento, con sello de tiempo NOM-151 y anclaje en Polygon."
        actions={<><LiveIndicator lastSync={ledger.lastSync} /><Button onClick={verify} disabled={checking} data-testid="ledger-verify-button">{checking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />}Verificar integridad</Button></>} />
      <div className="grid gap-4 md:grid-cols-3 mb-4">
        <Card><CardContent className="p-5">
          <div className="text-sm text-muted-foreground">Bloques en bitácora</div>
          <div className="text-3xl font-semibold font-display" data-testid="ledger-total">{ledger.data?.total ?? "—"}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-sm text-muted-foreground">Integridad de la cadena</div>
          {chain ? (chain.valid
            ? <div className="flex items-center gap-2 text-emerald-700 font-medium mt-1" data-testid="ledger-chain-valid"><ShieldCheck className="h-5 w-5" />Íntegra · {chain.checked} bloques</div>
            : <div className="flex items-center gap-2 text-rose-700 font-medium mt-1"><ShieldAlert className="h-5 w-5" />Ruptura en #{chain.broken_at}</div>)
            : <div className="text-sm mt-1">Presiona “Verificar integridad”</div>}
          {chain?.head && <div className="mt-1"><Hash value={chain.head} n={8} /></div>}
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-sm text-muted-foreground flex items-center gap-1.5"><KeyRound className="h-4 w-4" />Llave pública de firma</div>
          <div className="text-xs mt-1">{pk.data?.algorithm}</div>
          {pk.data && <CopyText value={pk.data.public_key_pem} display={`huella ${pk.data.fingerprint.slice(0, 16)}…`} testid="public-key-copy" />}
        </CardContent></Card>
      </div>
      <Tabs defaultValue="ledger">
        <TabsList><TabsTrigger value="ledger" data-testid="audit-tab-ledger">Bitácora blockchain</TabsTrigger><TabsTrigger value="webhooks" data-testid="audit-tab-webhooks">Eventos webhook</TabsTrigger></TabsList>
        <TabsContent value="ledger">
          <Card><CardContent className="p-4">
            {!ledger.data ? <Loading /> : (
              <div className="overflow-x-auto max-h-[600px]"><Table>
                <TableHeader className="sticky top-0 bg-white"><TableRow><TableHead>#</TableHead><TableHead>Evento</TableHead><TableHead>Folio</TableHead><TableHead className="hidden md:table-cell">Fecha</TableHead><TableHead>Hash datos</TableHead><TableHead className="hidden lg:table-cell">NOM-151</TableHead><TableHead>Tx Polygon</TableHead></TableRow></TableHeader>
                <TableBody>{ledger.data.items.map((e) => (
                  <TableRow key={e.index} data-testid={`ledger-row-${e.index}`}>
                    <TableCell className="font-mono text-xs">{e.index}</TableCell><TableCell className="text-xs">{e.event}</TableCell>
                    <TableCell className="font-mono text-xs">{e.folio}</TableCell><TableCell className="hidden md:table-cell text-xs">{fdate(e.timestamp)}</TableCell>
                    <TableCell><Hash value={e.data_hash} n={6} /></TableCell><TableCell className="hidden lg:table-cell font-mono text-[11px]">{e.nom151.constancia_id}</TableCell>
                    <TableCell><span className="flex items-center gap-1"><Hash value={e.anchor.tx_hash} n={6} />{e.anchor.simulated && <SimBadge />}</span></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table></div>
            )}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="webhooks">
          <Card><CardHeader className="pb-2"><CardTitle className="text-base">Sincronización bidireccional (WhatsApp, Cuenta Nu, Firebase)</CardTitle></CardHeader>
            <CardContent>
              {!events.data ? <Loading /> : (
                <div className="overflow-x-auto max-h-[560px]"><Table>
                  <TableHeader className="sticky top-0 bg-white"><TableRow><TableHead>Proveedor</TableHead><TableHead>Tipo</TableHead><TableHead>Origen</TableHead><TableHead>Recibido</TableHead><TableHead className="hidden md:table-cell">ID evento</TableHead></TableRow></TableHeader>
                  <TableBody>{events.data.map((e) => (
                    <TableRow key={e.event_id} data-testid="webhook-event-row">
                      <TableCell className="text-sm">{e.provider}</TableCell><TableCell className="font-mono text-xs">{e.type}</TableCell>
                      <TableCell>{e.origin === "simulador" ? <SimBadge label="SIMULADOR" /> : <span className="text-xs">webhook</span>}</TableCell>
                      <TableCell className="text-xs">{fdate(e.received_at)}</TableCell><TableCell className="hidden md:table-cell font-mono text-[11px] truncate max-w-[220px]">{e.event_id}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table></div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
